import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { EyeSlash, Eye } from "react-bootstrap-icons";
import { Form, Spinner } from "react-bootstrap";
import { registerUser, clearError } from "./slice";
import "./createAccount.css";
import PasswordRequirements from "../../components/WebApp/PasswordRequirements";
import {
  validateEmail,
  validatePassword,
} from "../../../utils/helperFunctions/inputValidations";

const CreateAccount = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { isLoading, createAccountError, success } = useSelector(
    (state) => state.createAccount
  );

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    firstName: "",
    lastName: "",
    termsAccepted: false,
    privacyAccepted: false,
  });

  // Add validation state
  const [validationErrors, setValidationErrors] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    firstName: "",
    lastName: "",
  });

  // Set the validation error for the email field if there is an error
  useEffect(() => {
    if (createAccountError) {
      setValidationErrors((prev) => ({
        ...prev,
        email: createAccountError.email,
      }));
    }
  }, [createAccountError]);

  useEffect(() => {
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  useEffect(() => {
    if (success) {
      navigate(`/verify-email`);
    }
  }, [success, navigate, formData.email]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    // Validate on input change
    if (name === "email") {
      if (createAccountError) {
        dispatch(clearError());
      }

      setValidationErrors((prev) => ({
        ...prev,
        email: !value
          ? "Email is required"
          : !validateEmail(value)
            ? "Please enter a valid email address"
            : "",
      }));
    } else if (name === "password") {
      setValidationErrors((prev) => ({
        ...prev,
        password: !value
          ? "Password is required"
          : !validatePassword(value)
            ? "Invalid password"
            : "",
        confirmPassword:
          formData.confirmPassword && value !== formData.confirmPassword
            ? "Passwords do not match"
            : "",
      }));
    } else if (name === "confirmPassword") {
      setValidationErrors((prev) => ({
        ...prev,
        confirmPassword: !value
          ? "Please confirm your password"
          : value !== formData.password
            ? "Passwords do not match"
            : "",
      }));
    } else if (name === "firstName") {
      setValidationErrors((prev) => ({
        ...prev,
        firstName: !value ? "First name is required" : "",
      }));
    } else if (name === "lastName") {
      setValidationErrors((prev) => ({
        ...prev,
        lastName: !value ? "Last name is required" : "",
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate all fields before submission
    const newValidationErrors = {
      email: !formData.email
        ? "Email is required"
        : !validateEmail(formData.email)
          ? "Please enter a valid email address"
          : "",
      password: !formData.password
        ? "Password is required"
        : !validatePassword(formData.password)
          ? "Invalid password"
          : "",
      confirmPassword: !formData.confirmPassword
        ? "Please confirm your password"
        : formData.confirmPassword !== formData.password
          ? "Passwords do not match"
          : "",
      firstName: !formData.firstName ? "First name is required" : "",
      lastName: !formData.lastName ? "Last name is required" : "",
    };

    setValidationErrors(newValidationErrors);

    // Check if there are any validation errors
    if (Object.values(newValidationErrors).some((error) => error !== "")) {
      return;
    }

    // Store password temporarily for auto-login after email verification
    sessionStorage.setItem("tempPassword", formData.password);

    const userData = {
      email: formData.email,
      password: formData.password,
      first_name: formData.firstName,
      last_name: formData.lastName,
      is_agree_to_terms_and_conditions: formData.termsAccepted,
      is_agree_to_privacy_policy: formData.privacyAccepted,
    };

    await dispatch(registerUser(userData));
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const validationFailed = !formData.termsAccepted || !formData.privacyAccepted;

  return (
    <div className="create-account-container">
      <h1 className="page-title">Create Account</h1>
      <Form onSubmit={handleSubmit} noValidate>
        <Form.Group className="mb-3">
          <Form.Label>Email*</Form.Label>
          <Form.Control
            type="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            isInvalid={!!validationErrors.email || !!createAccountError}
            required
          />
          <Form.Control.Feedback type="invalid">
            {validationErrors.email || createAccountError}
          </Form.Control.Feedback>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Password*</Form.Label>
          <div className="password-input-wrapper">
            <Form.Control
              type="text"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              isInvalid={!!validationErrors.password}
              style={{
                WebkitTextSecurity: showPassword ? "none" : "disc",
                MozTextSecurity: showPassword ? "none" : "disc",
                textSecurity: showPassword ? "none" : "disc",
              }}
              required
            />
            <span
              className="password-toggle-icon"
              onClick={togglePasswordVisibility}
            >
              {showPassword ? <Eye size={16} /> : <EyeSlash size={16} />}
            </span>
          </div>
          <Form.Control.Feedback type="invalid">
            {validationErrors.password}
          </Form.Control.Feedback>
          {formData.password && validationErrors.password ? (
            <PasswordRequirements password={formData.password} />
          ) : null}
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Confirm Password*</Form.Label>
          <div className="password-input-wrapper">
            <Form.Control
              type="text"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              isInvalid={!!validationErrors.confirmPassword}
              style={{
                WebkitTextSecurity: showConfirmPassword ? "none" : "disc",
                MozTextSecurity: showConfirmPassword ? "none" : "disc",
                textSecurity: showConfirmPassword ? "none" : "disc",
              }}
              required
            />
            <span
              className="password-toggle-icon"
              onClick={toggleConfirmPasswordVisibility}
            >
              {showConfirmPassword ? <Eye size={16} /> : <EyeSlash size={16} />}
            </span>
          </div>
          <Form.Control.Feedback type="invalid">
            {validationErrors.confirmPassword}
          </Form.Control.Feedback>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>First Name*</Form.Label>
          <Form.Control
            type="text"
            name="firstName"
            value={formData.firstName}
            onChange={handleInputChange}
            isInvalid={!!validationErrors.firstName}
            required
          />
          <Form.Control.Feedback type="invalid">
            {validationErrors.firstName}
          </Form.Control.Feedback>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Last Name*</Form.Label>
          <Form.Control
            type="text"
            name="lastName"
            value={formData.lastName}
            onChange={handleInputChange}
            isInvalid={!!validationErrors.lastName}
            required
          />
          <Form.Control.Feedback type="invalid">
            {validationErrors.lastName}
          </Form.Control.Feedback>
        </Form.Group>
        <hr className="divider" />
        <div className="mt-4 mb-2 text-small">
          Please review and accept the terms to proceed.
        </div>
        <Form.Group className="mb-2">
          <Form.Label className="flex">
            <Form.Check
              type="checkbox"
              name="termsAccepted"
              checked={formData.termsAccepted}
              onChange={handleInputChange}
              required
            />
            I agree to the&nbsp;
            <a
              href="https://www.morph-technologies.com/terms-conditions"
              target="_blank"
              rel="noopener noreferrer"
              className="text-button-link"
            >
              Terms and Conditions
            </a>
          </Form.Label>
        </Form.Group>

        <Form.Group className="mb-6">
          <Form.Label className="flex">
            <Form.Check
              type="checkbox"
              name="privacyAccepted"
              checked={formData.privacyAccepted}
              onChange={handleInputChange}
              required
            />
            I agree to the&nbsp;
            <a
              href="https://www.morph-technologies.com/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-button-link"
            >
              Privacy Policy
            </a>
          </Form.Label>
        </Form.Group>

        <button
          type="submit"
          disabled={isLoading || validationFailed}
          className="primary-action-button"
        >
          {isLoading ? (
            <Spinner
              as="span"
              animation="border"
              size="sm"
              role="status"
              aria-hidden="true"
              className="loading-button-spinner"
            />
          ) : (
            "Create Account"
          )}
        </button>

        <div className="text-center mt-4 text-small">
          Already have an account?{" "}
          <Link to="/login" className="text-button-link">
            Log In
          </Link>
        </div>
      </Form>
    </div>
  );
};

export default CreateAccount;
